import { useEffect } from "react";
import { Box, Alert } from "@mui/material";
import { useSelector } from "react-redux";
import { RootState, useAppDispatch } from "../../../store";
import {
  fetchContractInsights,
  fetchContractDashboard,
  fetchExpiringContracts,
  setExpiringContractsPage,
} from "../../../store/contractDashboardSlice";
import Sidebar from "../../../components/Sidebar";
import ContractPerformanceSection from "../../../components/ContractDashboard/ContractPerformanceSection";
import InsightsCard from "../../../components/ContractDashboard/InsightsCard";
import MetricsCard from "../../../components/ContractDashboard/MetricsCard";
import TopContractsByValue from "../../../components/ContractDashboard/TopContractsByValue";
import ExpiringContractsTable from "../../../components/ContractDashboard/ExpiringContractsTable";
import ContractComplianceByService from "../../../components/ContractDashboard/ContractComplianceByService";
import ValueLeakageTrend from "../../../components/ContractDashboard/ValueLeakageTrend";
import SpendUnderManagement from "../../../components/ContractDashboard/SpendUnderManagement";
import RenewalTimeliness from "../../../components/ContractDashboard/RenewalTimeliness";
import ContractRiskExposureTable from "../../../components/ContractDashboard/ContractRiskExposureTable";
import PageLoader from "../../../components/PageLoader";
import styles from "./ContractPerformance.module.scss";
import DashboardFilters from "../../../components/ContractDashboard/DashboardFilters";

const ContractPerformance = () => {
  const dispatch = useAppDispatch();
  const {
    contractInsights,
    contractDashboard,
    expiringContracts,
    expiringContractsPage,
    contractInsightsLoading,
    contractDashboardLoading,
    expiringContractsLoading,
    error,
  } = useSelector((state: RootState) => state.contractDashboard);
  const { contractDashboardFilters } = useSelector(
    (state: RootState) => state.filters,
  );

  useEffect(() => {
    const filters = {
      group_of_companies: contractDashboardFilters.groupCompany,
      contract_type: contractDashboardFilters.contractType,
    };

    dispatch(fetchContractInsights(filters));
    dispatch(fetchContractDashboard(filters));
    dispatch(fetchExpiringContracts({ page: 1, limit: 5, ...filters }));
  }, [
    dispatch,
    contractDashboardFilters.groupCompany,
    contractDashboardFilters.contractType,
  ]);

  const handlePageChange = (page: number) => {
    // Don't fetch if clicking on the same page
    if (page === expiringContractsPage) {
      return;
    }

    const filters = {
      group_of_companies: contractDashboardFilters.groupCompany,
      contract_type: contractDashboardFilters.contractType,
    };

    dispatch(setExpiringContractsPage(page));
    dispatch(fetchExpiringContracts({ page, limit: 5, ...filters }));
  };

  // Aggregate loading: Show loader until ALL APIs complete successfully
  // Only show full page loader when:
  const isLoading =
    (contractInsightsLoading ||
      contractDashboardLoading ||
      expiringContractsLoading) &&
    (contractInsights === null ||
      contractDashboard === null ||
      expiringContracts === null);

  if (isLoading) {
    return <PageLoader label="Loading contract performance..." />;
  }

  if (error) {
    return (
      <Box className={styles.contractPerformanceContainer}>
        <Box className={styles.contentWrapper}>
          <Sidebar activeNav="dashboard" />
          <Box className={styles.mainContent}>
            <Alert severity="error" sx={{ m: 2 }}>
              {error}
            </Alert>
          </Box>
        </Box>
      </Box>
    );
  }

  if (
    contractInsights === null ||
    contractDashboard === null ||
    expiringContracts === null
  ) {
    return null;
  }

  // Ensure contractInsights is an array
  const insightsArray = Array.isArray(contractInsights) ? contractInsights : [];

  // Map API insight type to InsightsCard type
  const getCardType = (
    insightType: string,
  ): "risk" | "leakage" | "bottleneck" => {
    const typeMap: Record<string, "risk" | "leakage" | "bottleneck"> = {
      warning: "risk",
      info: "leakage",
      success: "bottleneck",
    };
    return typeMap[insightType] || "risk";
  };

  return (
    <Box className={styles.contractPerformanceContainer}>
      <Box className={styles.contentWrapper}>
        <Sidebar activeNav="dashboard" />
        <Box className={styles.mainContent}>
          <Box className={styles.dashboardContainer}>
            <DashboardFilters
              title="Contract Performance"
              subtitle="Value protection, compliance, renewal discipline, and risk management."
            />

            {insightsArray.length > 0 && (
              <Box className={styles.insightsSection}>
                <Box className={styles.insightsGrid}>
                  {insightsArray.map((insight) => (
                    <InsightsCard
                      key={insight.title}
                      type={getCardType(insight.type)}
                      title={insight.title}
                      description={insight.message}
                    />
                  ))}
                </Box>
              </Box>
            )}

            <Box className={styles.metricsSection}>
              <Box className={styles.metricsGrid}>
                <MetricsCard
                  title="Total Active Contracts"
                  value={contractDashboard.contract_statistics.active_contracts.count.toString()}
                  amount={`$${contractDashboard.contract_statistics.active_contracts.active_contracts_value.toFixed(1)}M`}
                  percentage={
                    contractDashboard.contract_statistics.active_contracts
                      .change
                  }
                  percentageLabel="vs previous quarter"
                />
                <MetricsCard
                  title="Expiring in 0-15 days"
                  value={contractDashboard.contract_statistics.expiring_contracts.count.toString()}
                  amount={`$${contractDashboard.contract_statistics.expiring_contracts.expiring_contracts_value.toFixed(1)}M`}
                  percentage={
                    contractDashboard.contract_statistics.expiring_contracts
                      .change
                  }
                  percentageLabel="vs previous quarter"
                />
                <MetricsCard
                  title="Expired Contracts"
                  value={contractDashboard.contract_statistics.expired_contracts.count.toString()}
                  amount={`$${contractDashboard.contract_statistics.expired_contracts.expired_contracts_value.toFixed(1)}K`}
                  percentage={
                    contractDashboard.contract_statistics.expired_contracts
                      .change
                  }
                  percentageLabel="vs previous quarter"
                />
                <MetricsCard
                  title="UAE Jurisdiction"
                  value={contractDashboard.contract_statistics.uae_jurisdiction.count.toString()}
                  amount={`${contractDashboard.contract_statistics.uae_jurisdiction.percentage}% of total`}
                  percentage={
                    contractDashboard.contract_statistics.uae_jurisdiction
                      .change
                  }
                  percentageLabel="vs previous quarter"
                />
                <MetricsCard
                  title="Payment Term <60 days"
                  value={contractDashboard.contract_statistics.payment_terms_less_than_60_days.count.toString()}
                  amount={`${contractDashboard.contract_statistics.payment_terms_less_than_60_days.percentage}%`}
                />
                <MetricsCard
                  title="Exclusivity Agreements"
                  value={contractDashboard.contract_statistics.exclusivity_aggrements.count.toString()}
                  amount={`${contractDashboard.contract_statistics.exclusivity_aggrements.percentage}%`}
                />
                <MetricsCard
                  title="Auto-Renewal Enabled"
                  value={contractDashboard.contract_statistics.auto_renewal_enabled.count.toString()}
                  amount={`${contractDashboard.contract_statistics.auto_renewal_enabled.percentage}%`}
                />
                <MetricsCard
                  title="Contract Compliance Rate"
                  value={`${contractDashboard.contract_statistics.contract_compliance_rate.count}%`}
                  percentage={
                    contractDashboard.contract_statistics
                      .contract_compliance_rate.percentage > 0
                      ? `+${contractDashboard.contract_statistics.contract_compliance_rate.percentage}%`
                      : `${contractDashboard.contract_statistics.contract_compliance_rate.percentage}%`
                  }
                />
              </Box>
            </Box>

            <Box className={styles.tableSection}>
              <TopContractsByValue
                data={contractDashboard.top_contracts_by_deal_value}
              />
            </Box>

            <Box className={styles.tableSection}>
              <ExpiringContractsTable
                data={expiringContracts}
                currentPage={expiringContractsPage}
                onPageChange={handlePageChange}
              />
            </Box>

            <Box className={styles.chartsSection}>
              <ContractComplianceByService
                data={contractDashboard.compliance_by_category}
              />
              <ValueLeakageTrend data={contractDashboard.valueLeakageTrend} />
            </Box>

            <Box className={styles.chartsSection}>
              <SpendUnderManagement
                data={contractDashboard.spendUnderManagement}
              />
              <RenewalTimeliness data={contractDashboard.renewalTimeliness} />
            </Box>

            <Box className={styles.tableSection}>
              <ContractRiskExposureTable
                data={contractDashboard.contractRiskExposure}
              />
            </Box>
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

export default ContractPerformance;
